// Role tiers across both org types (mechanical contractor + facility owner):
//   admin tier   → admin (mechanical), executive (facility)   — full control
//   manager tier → manager, facilities_manager
//   tech tier    → technician, maintenance
const ADMIN_TIER = ["admin", "executive"];
const MANAGER_TIER = ["manager", "facilities_manager"];

export const isAdminTier = (role) => ADMIN_TIER.includes(role);
export const isManagerTier = (role) => MANAGER_TIER.includes(role);

// Facility intra-org visibility: an executive sees all of the org's buildings;
// facilities_manager / maintenance see only buildings assigned to them
// (building.assignedUsers contains their uid).
export const visibleBuildings = (role, uid, buildings = []) =>
  role === "executive"
    ? buildings
    : buildings.filter((b) => (b.assignedUsers || []).includes(uid));

// Capabilities that are opt-in (default OFF — must be explicitly granted by
// an executive) for a specific role, even though that role's tier would
// otherwise grant them. Adding buildings org-wide is a significant action, so
// a facilities manager only gets it when the executive checks it.
const OPT_IN_CAPS = {
  facilities_manager: ["addLocations"],
};

// Effective capability check honoring role-based defaults:
//   admin tier   → always true (unrestricted; assigns everyone else)
//   manager tier → true UNLESS explicitly revoked (capabilities[key] === false)
//   tech tier    → false UNLESS explicitly granted (capabilities[key] === true)
//   opt-in cap   → false UNLESS explicitly granted, regardless of tier
//
// Used by Manage Users (checkbox state + toggle), the app's button
// gating, and mirrors what the firestore.rules enforce server-side.
export const hasCapability = (role, capabilities, capKey) => {
  if (ADMIN_TIER.includes(role)) return true;
  const explicit = capabilities?.[capKey];
  if ((OPT_IN_CAPS[role] || []).includes(capKey)) return explicit === true;
  if (MANAGER_TIER.includes(role)) return explicit !== false;
  return explicit === true;
};

// Events that default ON for the tech tier too (not just admin/manager).
// These are alerts a tech really wants to see by default — direct
// involvement (a job assigned to them, their PO approved) or org-wide
// communication (messages, service requests). Tech can still opt out.
const TECH_DEFAULT_ON_EVENTS = new Set([
  "new_message",
  "job_assigned_to_me",
  "po_request_approved",
  "service_accepted",
  "service_declined",
  "service_requested",
]);

// Whether an alert of type `eventKey` should land in this user's bell.
// Mirrors hasCapability: admin/manager default-on, tech default-off, with
// each user able to explicitly override via their notificationPreferences.
// Certain "you really want this" events default-on for tech too.
export const wantsAlert = (role, prefs, eventKey) => {
  const explicit = prefs?.[eventKey];
  if (ADMIN_TIER.includes(role)) return explicit !== false;
  if (MANAGER_TIER.includes(role)) return explicit !== false;
  if (TECH_DEFAULT_ON_EVENTS.has(eventKey)) return explicit !== false;
  return explicit === true;
};

// True when `doc` was created by `user`. Used to scope down data lists on
// pages where a tech (or any user without the matching viewAllX capability)
// should only see their own quotes / POs / service tickets. Checks both
// createdByUid (preferred; immutable) and createdBy (email; older docs).
export const isMine = (doc, user) => {
  if (!doc || !user) return false;
  if (doc.createdByUid && doc.createdByUid === user.uid) return true;
  if (doc.createdBy && user.email && doc.createdBy === user.email) return true;
  return false;
};

// Derive a human display name from a profile, falling back to the email if
// nothing's set yet. Used everywhere we'd otherwise show a raw email
// (message bubbles, "by [creator]" stamps, the header, etc.).
export const displayName = (profile, emailFallback) => {
  const first = profile?.firstName?.trim() || "";
  const last = profile?.lastName?.trim() || "";
  const full = [first, last].filter(Boolean).join(" ").trim();
  return full || emailFallback || "Unknown";
};
